import { initializeApp, FirebaseError } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  updateProfile, 
  sendPasswordResetEmail, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithRedirect, 
  getRedirectResult 
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  deleteDoc, 
  addDoc, 
  orderBy, 
  limit, 
  startAfter,
  DocumentSnapshot,
  getCountFromServer
} from 'firebase/firestore';
import { getAnalytics } from "firebase/analytics";
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { CommunityPost, CommunityComment, PaginatedResult } from '../types/community';
import { DirectConversation, DirectMessage } from '../types/messaging';
import { UserRole, UserProfile } from '../types/user';

// Firebase yapılandırması - Firebase tarafından verilen doğru değerler
const firebaseConfig = {
  apiKey: "AIzaSyAkBdrzL7r116zFeT32nDJ3qBJmxfwJR1s",
  authDomain: "vagonla-core.firebaseapp.com",
  projectId: "vagonla-core",
  storageBucket: "vagonla-core.firebasestorage.app",
  messagingSenderId: "456174139452",
  appId: "1:456174139452:web:4fb5b604c1bb5973c4ab39",
  measurementId: "G-W0T251HFYD"
};

// Firebase'i başlat
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const analytics = getAnalytics(app);
const storage = getStorage(app);

// Firebase hata kodlarını Türkçe mesajlara çeviren fonksiyon
export const getFirebaseErrorMessage = (error: FirebaseError): string => {
  switch (error.code) {
    // Auth hataları
    case 'auth/email-already-in-use':
      return 'Bu e-posta adresi zaten kullanılıyor.';
    case 'auth/invalid-email':
      return 'Geçersiz e-posta adresi.';
    case 'auth/weak-password':
      return 'Şifre çok zayıf. En az 6 karakter uzunluğunda olmalıdır.';
    case 'auth/user-not-found':
      return 'Bu e-posta adresine kayıtlı kullanıcı bulunamadı.';
    case 'auth/wrong-password':
      return 'Hatalı şifre girdiniz.';
    case 'auth/too-many-requests':
      return 'Çok fazla başarısız giriş denemesi. Lütfen bir süre bekleyip tekrar deneyin.';
    case 'auth/user-disabled':
      return 'Bu kullanıcı hesabı devre dışı bırakılmış.';
    case 'auth/operation-not-allowed':
      return 'Bu işlem geçici olarak devre dışı bırakılmış.';
    case 'auth/network-request-failed':
      return 'Ağ hatası. İnternet bağlantınızı kontrol edin.';
    case 'auth/configuration-not-found':
      return 'Firebase yapılandırma hatası. Lütfen yönetici ile iletişime geçin.';
    case 'auth/account-exists-with-different-credential':
      return 'Bu e-posta adresi başka bir giriş yöntemi ile zaten kullanılıyor. Lütfen diğer giriş yöntemini deneyin.';
    case 'auth/popup-closed-by-user':
      return 'Giriş işlemi penceresi kapatıldı. Lütfen tekrar deneyin.';
    case 'auth/cancelled-popup-request':
      return 'Önceki bir giriş işlemi devam ediyor. Lütfen bekleyin.';
    case 'auth/popup-blocked':
      return 'Giriş penceresi tarayıcı tarafından engellendi. Lütfen popup izinlerini kontrol edin.';
    case 'auth/credential-already-in-use':
      return 'Bu hesap başka bir kullanıcı tarafından zaten kullanılıyor.';

    // Firestore hataları
    case 'firestore/unavailable':
      return 'Firebase Firestore şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.';
    case 'firestore/data-loss':
      return 'Veri kaybı oluştu. Lütfen işleminizi tekrarlayın.';
    case 'firestore/permission-denied':
      return 'Bu işlem için yetkiniz bulunmuyor.';
    case 'firestore/unauthenticated':
      return 'Oturum süreniz doldu. Lütfen tekrar giriş yapın.';
      
    // Genel hatalar
    case 'app/network-error':
      return 'İnternet bağlantınızı kontrol edin ve tekrar deneyin.';
    case 'app/quota-exceeded':
      return 'Sunucu yoğunluğu nedeniyle işleminiz şu anda gerçekleştirilemiyor. Lütfen daha sonra tekrar deneyin.';
    
    default:
      return `Bir hata oluştu: ${error.message}`;
  }
};

// Google ile giriş yapma fonksiyonu
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await signInWithPopup(auth, provider);
    
    // Kullanıcı Firebase Auth'a kaydedildi, Firestore'da profil var mı kontrol et
    const userProfile = await getUserProfile(result.user.uid);
    
    // Eğer profil yoksa, Google bilgileriyle yeni bir profil oluştur
    if (!userProfile) {
      await createUserProfile(result.user.uid, {
        uid: result.user.uid,
        email: result.user.email || '',
        displayName: result.user.displayName || 'Google Kullanıcısı',
        role: 'user',
        createdAt: new Date().toISOString(),
        isPremium: false,
        photoURL: result.user.photoURL || '',
        provider: 'google'
      });
      console.log('Google kullanıcısı için yeni profil oluşturuldu');
    }
    
    return result.user;
  } catch (error) {
    if (error instanceof FirebaseError) {
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Giriş penceresi kapatıldı. Lütfen tekrar deneyin.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        throw new Error('İşlem iptal edildi. Lütfen tekrar deneyin.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Tarayıcınız popup penceresini engelledi. Popup izinlerini kontrol edin.');
      } else {
        throw new Error(getFirebaseErrorMessage(error));
      }
    }
    throw error;
  }
};

// Yönlendirme ile Google kimlik doğrulama (mobil uyumlu alternatif)
export const signInWithGoogleRedirect = async () => {
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    console.log('Google yönlendirme ile giriş başlatılıyor...');
    await signInWithRedirect(auth, provider);
    // Yönlendirme işlemi gerçekleşecek, kullanıcı geri geldiğinde sonuç kontrol edilecek
  } catch (error) {
    console.error("Google yönlendirme ile giriş yaparken hata:", error);
    throw error;
  }
};

// Yönlendirme sonrası sonucu işle (getRedirectResult)
export const handleGoogleRedirectResult = async () => {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      console.log('Google yönlendirmesi başarılı');
      return result.user;
    }
    return null;
  } catch (error) {
    console.error("Google yönlendirme sonucu işlenirken hata:", error);
    throw error;
  }
};

// Kimlik doğrulama işlemleri
export const registerUser = async (email: string, password: string, displayName: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Kullanıcı adını güncelle
    await updateProfile(user, { displayName });
    
    // Firestore'da kullanıcı profili oluştur
  await createUserProfile(user.uid, {
      uid: user.uid,
      email: email,
      displayName: displayName,
      role: 'user', // Varsayılan rol
      createdAt: new Date().toISOString(),
  isPremium: false,
  privacy: 'members'
    });
    
    return user;
  } catch (error) {
    console.error("Kayıt olurken hata:", error);
    if (error instanceof FirebaseError) {
      throw new Error(getFirebaseErrorMessage(error));
    }
    throw error;
  }
};

export const loginUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error("Giriş yaparken hata:", error);
    if (error instanceof FirebaseError) {
      throw new Error(getFirebaseErrorMessage(error));
    }
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    console.error("Çıkış yaparken hata:", error);
    throw error;
  }
};

export const resetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    console.error("Şifre sıfırlarken hata:", error);
    throw error;
  }
};

// Kullanıcı profili işlemleri
export const createUserProfile = async (userId: string, profileData: UserProfile) => {
  try {
    await setDoc(doc(db, "users", userId), profileData);
    return true;
  } catch (error) {
    console.error("Kullanıcı profili oluşturulurken hata:", error);
    throw error;
  }
};

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const docRef = doc(db, "users", userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as UserProfile;
    } else {
      // Kullanıcı profili bulunamadı durumunda varsayılan temel profil oluştur
      console.log("Kullanıcı profili bulunamadı! Temel profil oluşturuluyor...");
      const user = auth.currentUser;
      if (user) {
        // Eğer auth.currentUser varsa, temel bir profil oluştur
        const basicProfile: UserProfile = {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || 'Kullanıcı',
          role: 'user',
          createdAt: new Date().toISOString(),
          isPremium: false,
          privacy: 'members'
        };
        
        // Temel profili Firestore'a kaydet
        try {
          await setDoc(doc(db, "users", user.uid), basicProfile);
          console.log("Temel kullanıcı profili oluşturuldu");
          return basicProfile;
        } catch (error) {
          console.error("Temel profil oluşturulurken hata:", error);
          // Hata oluşsa bile işlemi devam ettirelim, en azından UI'da bir sorun olmasın
          return basicProfile;
        }
      }
      return null;
    }
  } catch (error) {
    console.error("Kullanıcı profili alınırken hata:", error);
    
    // Her türlü hata durumunu ele alalım
    if (error instanceof FirebaseError) {
      // Bağlantı hatalarında
      if (error.code === 'firestore/internal' || 
          error.message.includes('network')) {
        console.warn('Firestore bağlantı sorunu, temel profil döndürülüyor');
        
        // Auth user bilgisiyle temel bir profil döndürelim
        const user = auth.currentUser;
        if (user) {
          return {
            uid: user.uid,
            email: user.email || '',
            displayName: user.displayName || 'Kullanıcı',
            role: 'user',
            createdAt: new Date().toISOString(),
            isPremium: false,
            privacy: 'members'
          };
        }
      }
      
      console.error("Firebase hatası:", getFirebaseErrorMessage(error));
    }
    
    // En son çare: null yerine temel bir profil döndürelim
    const user = auth.currentUser;
    if (user) {
      console.warn("Hata olmasına rağmen temel profil döndürülüyor");
      return {
        uid: user.uid,
        email: user.email || '',
        displayName: user.displayName || 'Kullanıcı',
        role: 'user', 
        createdAt: new Date().toISOString(),
  isPremium: false,
  privacy: 'members'
      };
    }
    
    return null;
  }
};

export const updateUserProfile = async (userId: string, updateData: Partial<UserProfile>) => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, updateData);
    return true;
  } catch (error) {
    console.error("Kullanıcı profili güncellenirken hata:", error);
    throw error;
  }
};

// Avatar yükleme
export const uploadUserAvatar = async (userId: string, file: File): Promise<string> => {
  if (!file) throw new Error('Dosya bulunamadı');
  // Basit boyut kontrolü (3MB)
  if (file.size > 3 * 1024 * 1024) throw new Error('Maksimum dosya boyutu 3MB');
  const safeName = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
  const fileRef = ref(storage, `avatars/${userId}/${Date.now()}-${safeName}`);
  await uploadBytes(fileRef, file);
  const url = await getDownloadURL(fileRef);
  await updateUserProfile(userId, { photoURL: url });
  return url;
};

// --- Username Yönetimi ---
const USERNAME_REGEX = /^[a-z0-9]{3,24}$/; // sadece küçük harf ve rakam

export const isUsernameValid = (username: string) => USERNAME_REGEX.test(username);

export const isUsernameAvailable = async (username: string): Promise<boolean> => {
  // Username'ler users koleksiyonunda benzersiz alan olarak saklanacak.
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('username', '==', username));
  const snap = await getDocs(q);
  return snap.empty; // hiç eşleşme yoksa uygun
};

export const setUsernameForUser = async (userId: string, username: string): Promise<{ success: boolean; reason?: string }> => {
  const cleaned = username.trim();
  if (!isUsernameValid(cleaned)) return { success: false, reason: 'Geçersiz kullanıcı adı formatı' };
  if (!(await isUsernameAvailable(cleaned))) return { success: false, reason: 'Kullanıcı adı zaten alınmış' };
  try {
    await updateUserProfile(userId, { username: cleaned });
    return { success: true };
  } catch (e) {
    return { success: false, reason: 'Kayıt sırasında hata' };
  }
};

export const getUserProfileByUsername = async (username: string): Promise<UserProfile | null> => {
  const cleaned = username.trim();
  if (!isUsernameValid(cleaned)) return null;
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('username', '==', cleaned), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return docSnap.data() as UserProfile;
};

export const upgradeToAdmin = async (userId: string) => {
  try {
    await updateUserProfile(userId, { role: 'admin' });
    return true;
  } catch (error) {
    console.error("Yönetici yetkileri verilirken hata:", error);
    throw error;
  }
};

export const upgradeToPremium = async (userId: string) => {
  try {
    await updateUserProfile(userId, { 
      isPremium: true,
      premiumSince: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error("Premium üyeliğe yükseltilirken hata:", error);
    throw error;
  }
};

export const getAllUsers = async (): Promise<UserProfile[]> => {
  try {
    const usersCollection = collection(db, "users");
    const userSnapshot = await getDocs(usersCollection);
    const userList = userSnapshot.docs.map(doc => doc.data() as UserProfile);
    return userList;
  } catch (error) {
    console.error("Kullanıcılar alınırken hata:", error);
    throw error;
  }
};

export const getUsersByRole = async (role: UserRole): Promise<UserProfile[]> => {
  try {
    const usersCollection = collection(db, "users");
    const roleQuery = query(usersCollection, where("role", "==", role));
    const querySnapshot = await getDocs(roleQuery);
    const userList = querySnapshot.docs.map(doc => doc.data() as UserProfile);
    return userList;
  } catch (error) {
    console.error(`${role} rolündeki kullanıcılar alınırken hata:`, error);
    throw error;
  }
};

// Gelişmiş kullanıcı yönetim işlemleri
export const deleteUserAccount = async (userId: string): Promise<boolean> => {
  try {
    // Önce Firestore'daki kullanıcı belgesini sil
    await deleteDoc(doc(db, "users", userId));
    
    // Eğer silme işlemi admin tarafından yapılıyorsa, Auth'tan da kullanıcıyı silmek gerekir
    // Bu işlem sadece Firebase Admin SDK ile yapılabilir veya kullanıcının kendisi tarafından
    // Burada basitçe Firestore'dan silme işlemi yapıyoruz
    console.log(`${userId} ID'li kullanıcı başarıyla silindi.`);
    return true;
  } catch (error) {
    console.error("Kullanıcı silinirken hata:", error);
    throw error;
  }
};

export const suspendUser = async (userId: string, reason: string): Promise<boolean> => {
  try {
    await updateUserProfile(userId, { 
      suspended: true, 
      suspensionReason: reason,
      suspendedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error("Kullanıcı askıya alınırken hata:", error);
    throw error;
  }
};

export const unsuspendUser = async (userId: string): Promise<boolean> => {
  try {
    await updateUserProfile(userId, { 
      suspended: false, 
      suspensionReason: null,
      suspendedAt: null
    });
    return true;
  } catch (error) {
    console.error("Kullanıcı askıdan kaldırılırken hata:", error);
    throw error;
  }
};

export const downgradeFromPremium = async (userId: string): Promise<boolean> => {
  try {
    await updateUserProfile(userId, { 
      isPremium: false,
      premiumSince: null
    });
    return true;
  } catch (error) {
    console.error("Premium üyelik düşürülürken hata:", error);
    throw error;
  }
};

// Sistem yönetim işlemleri
export const addSystemAnnouncement = async (announcement: {
  title: string;
  message: string;
  startDate: Date;
  endDate: Date;
  targetUserRoles?: UserRole[];
  targetCities?: string[];
  importance: 'low' | 'medium' | 'high';
}): Promise<string> => {
  try {
    const announcementData = {
      ...announcement,
      createdAt: new Date().toISOString(),
      active: true
    };
    
    const docRef = await addDoc(collection(db, "system_announcements"), announcementData);
    return docRef.id;
  } catch (error) {
    console.error("Sistem duyurusu eklenirken hata:", error);
    throw error;
  }
};

export const getUserActivityStats = async (): Promise<{
  totalUsers: number;
  activeUsers: number;
  premiumUsers: number;
  adminUsers: number;
  newUsersLast30Days: number;
}> => {
  try {
    const usersCollection = collection(db, "users");
    
    // Toplam kullanıcı sayısı
    const totalSnapshot = await getCountFromServer(usersCollection);
    const totalUsers = totalSnapshot.data().count;
    
    // Premium kullanıcı sayısı
    const premiumQuery = query(usersCollection, where("isPremium", "==", true));
    const premiumSnapshot = await getCountFromServer(premiumQuery);
    const premiumUsers = premiumSnapshot.data().count;
    
    // Admin kullanıcı sayısı
    const adminQuery = query(usersCollection, where("role", "==", "admin"));
    const adminSnapshot = await getCountFromServer(adminQuery);
    const adminUsers = adminSnapshot.data().count;
    
    // Son 30 gün içinde kaydolan kullanıcılar
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const newUsersQuery = query(usersCollection, where("createdAt", ">=", thirtyDaysAgo.toISOString()));
    const newUsersSnapshot = await getCountFromServer(newUsersQuery);
    const newUsersLast30Days = newUsersSnapshot.data().count;
    
    // Aktif kullanıcılar (son 7 gün içinde giriş yapmış)
    // Not: Bu veriyi tutmak için ayrı bir koleksiyon gereklidir
    // Burada örnek olarak total'in yarısını döndürüyoruz
    const activeUsers = Math.floor(totalUsers / 2);
    
    return {
      totalUsers,
      activeUsers,
      premiumUsers,
      adminUsers,
      newUsersLast30Days
    };
    
  } catch (error) {
    console.error("Kullanıcı istatistikleri alınırken hata:", error);
    throw error;
  }
};

// Şehir yönetim işlemleri
export const updateCitySettings = async (cityId: string, settings: {
  isActive: boolean;
  features: string[];
  apiEndpoint?: string;
}): Promise<boolean> => {
  try {
    await updateDoc(doc(db, "cities", cityId), settings);
    return true;
  } catch (error) {
    console.error("Şehir ayarları güncellenirken hata:", error);
    throw error;
  }
};

// Log ve denetim işlemleri
export const logAdminAction = async (adminId: string, action: string, details: any): Promise<void> => {
  try {
    await addDoc(collection(db, "admin_logs"), {
      adminId,
      action,
      details,
      timestamp: new Date().toISOString(),
      ipAddress: "API tarafından alınacak" // Gerçek uygulamada API'den alınmalı
    });
  } catch (error) {
    console.error("Admin işlemi kaydedilirken hata:", error);
    // Loglar kritik olmadığı için hata fırlatmıyoruz, sadece konsola yazıyoruz
  }
};

// Gelişmiş kullanıcı sorgulama
export const searchUsers = async (
  criteria: {
    displayName?: string;
    email?: string;
    role?: UserRole;
    isPremium?: boolean;
    suspended?: boolean;
  }, 
  pagination: {
    pageSize: number;
    startAfterDoc?: DocumentSnapshot;
  }
): Promise<{
  users: UserProfile[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}> => {
  try {
    let userQuery: any = collection(db, "users");
    const filters = [];
    
    if (criteria.displayName) {
      // Firestore tam metin araması desteklemediğinden, daha karmaşık bir çözüm gerekebilir
      // Basit bir başlangıç/içerir kontrolü için:
      const displayNameLower = criteria.displayName.toLowerCase();
      filters.push(where("displayNameLower", ">=", displayNameLower));
      filters.push(where("displayNameLower", "<=", displayNameLower + '\uf8ff'));
    }
    
    if (criteria.email) {
      filters.push(where("email", "==", criteria.email));
    }
    
    if (criteria.role) {
      filters.push(where("role", "==", criteria.role));
    }
    
    if (criteria.isPremium !== undefined) {
      filters.push(where("isPremium", "==", criteria.isPremium));
    }
    
    if (criteria.suspended !== undefined) {
      filters.push(where("suspended", "==", criteria.suspended));
    }
    
    // Filtreleri query'ye ekle
    if (filters.length > 0) {
      userQuery = query(userQuery, ...filters);
    }
    
    // Sayfalama ve sıralama
    userQuery = query(userQuery, orderBy("createdAt", "desc"), limit(pagination.pageSize));
    
    // Belirli bir belgeden sonra başlatma (sayfalama için)
    if (pagination.startAfterDoc) {
      userQuery = query(userQuery, startAfter(pagination.startAfterDoc));
    }
    
    const querySnapshot = await getDocs(userQuery);
    const users: UserProfile[] = [];
    let lastDoc: DocumentSnapshot | null = null;
    
    querySnapshot.forEach((doc) => {
      users.push(doc.data() as UserProfile);
      // Tip uyumsuzluğu hatasını düzeltmek için as kullanarak tip dönüşümü yapıyoruz
      lastDoc = doc as unknown as DocumentSnapshot;
    });
    
    // Daha fazla sonuç olup olmadığını kontrol et
    const hasMore = users.length === pagination.pageSize;
    
    return { users, lastDoc, hasMore };
    
  } catch (error) {
    console.error("Kullanıcılar aranırken hata:", error);
    throw error;
  }
};

// ================== Topluluk (Community) ==================
// Koleksiyon adları
const POSTS_COL = 'community_posts';
const COMMENTS_COL = 'community_comments';
const DM_CONV_COL = 'dm_conversations';
const DM_MSGS_COL = 'dm_messages';

// Post oluştur
export const createCommunityPost = async (data: { content: string; tags?: string[]; cityId?: string; cityCodeFallback?: string }) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Oturum gerekli');
  const profile = await getUserProfile(user.uid);
  const now = new Date().toISOString();
  const resolvedCityId = data.cityId && data.cityId !== '' ? data.cityId : (data.cityCodeFallback || null);
  const postDoc = await addDoc(collection(db, POSTS_COL), {
    authorId: user.uid,
    authorDisplayName: profile?.displayName || 'Kullanıcı',
    authorUsername: (profile as any)?.username || null,
    authorPhotoURL: profile?.photoURL || null,
  authorAvatarVisibility: (profile as any)?.avatarVisibility || 'everyone',
    content: data.content.trim(),
    tags: data.tags || [],
    cityId: resolvedCityId, // Boş kalırsa null yerine cityCode fallback
    cityCode: data.cityCodeFallback || null,
    createdAt: now,
    lastActivityAt: now,
    likesCount: 0,
    commentsCount: 0
  });
  return postDoc.id;
};

// Postları listele (sayfalama)
export const listCommunityPosts = async (opts: { pageSize?: number; lastDoc?: DocumentSnapshot | null; cityId?: string }) : Promise<PaginatedResult<CommunityPost>> => {
  const size = opts.pageSize || 10;
  let qBase: any = collection(db, POSTS_COL);
  const filters: any[] = [];
  const effectiveCity = opts.cityId && opts.cityId !== '' ? opts.cityId : null;
  if (effectiveCity) filters.push(where('cityId', '==', effectiveCity));
  if (filters.length) qBase = query(qBase, ...filters);
  qBase = query(qBase, orderBy('lastActivityAt', 'desc'), limit(size));
  if (opts.lastDoc) qBase = query(qBase, startAfter(opts.lastDoc));
  try {
    const snap = await getDocs(qBase);
    const items: CommunityPost[] = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
    return { items, lastDoc: snap.docs[snap.docs.length-1] || null, hasMore: items.length === size };
  } catch (e: any) {
    if (e?.code === 'failed-precondition') {
      // Fallback: index yoksa tüm postlardan küçük bir subset çekip filtrele
      if (effectiveCity) {
        try {
          const fallbackSnap = await getDocs(query(collection(db, POSTS_COL), orderBy('lastActivityAt', 'desc'), limit(size * 2)));
          const raw: CommunityPost[] = fallbackSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
          const filtered = raw.filter(p => (p as any).cityId === effectiveCity).slice(0, size);
          return { items: filtered, lastDoc: null, hasMore: filtered.length === size };
        } catch {
          throw new Error('Şehir filtreli sorgu için Firestore index gerekli (cityId ASC + lastActivityAt DESC).');
        }
      }
      throw new Error('Şehir filtreli sorgu için Firestore index gerekli (cityId ASC + lastActivityAt DESC).');
    }
    throw e;
  }
};

// Yorum ekle
export const addCommentToPost = async (postId: string, content: string) => {
  const user = auth.currentUser; if (!user) throw new Error('Oturum gerekli');
  const profile = await getUserProfile(user.uid);
  const now = new Date().toISOString();
  await addDoc(collection(db, COMMENTS_COL), {
    postId,
    userId: user.uid,
    displayName: profile?.displayName || 'Kullanıcı',
    photoURL: profile?.photoURL || null,
  avatarVisibility: (profile as any)?.avatarVisibility || 'everyone',
    content: content.trim(),
    createdAt: now
  });
  // yorum sayısı ve lastActivity güncelle
  await updateDoc(doc(db, POSTS_COL, postId), {
    commentsCount: ( (await getDoc(doc(db, POSTS_COL, postId))).data()?.commentsCount || 0) + 1,
    lastActivityAt: now
  });
};

// Post yorumlarını getir
export const listCommentsForPost = async (postId: string, opts: { pageSize?: number; lastDoc?: DocumentSnapshot | null } = {}): Promise<PaginatedResult<CommunityComment>> => {
  const size = opts.pageSize || 10;
  let qBase: any = query(collection(db, COMMENTS_COL), where('postId', '==', postId), orderBy('createdAt', 'asc'), limit(size));
  if (opts.lastDoc) qBase = query(qBase, startAfter(opts.lastDoc));
  const snap = await getDocs(qBase);
  const items: CommunityComment[] = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
  return { items, lastDoc: snap.docs[snap.docs.length-1] || null, hasMore: items.length === size };
};

// Basit like (optimistic yok - geliştirme aşaması)
export const likeCommunityPost = async (postId: string) => {
  // Gerçek uygulamada kullanıcı bazlı like kaydı tutulmalı (post_likes koleksiyonu)
  const refDoc = doc(db, POSTS_COL, postId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) throw new Error('Post bulunamadı');
  const current = snap.data()?.likesCount || 0;
  await updateDoc(refDoc, { likesCount: current + 1, lastActivityAt: new Date().toISOString() });
};

// Post sil (sadece yazar veya admin senaryosu için)
export const deleteCommunityPost = async (postId: string) => {
  const user = auth.currentUser; if(!user) throw new Error('Oturum gerekli');
  const ref = doc(db, POSTS_COL, postId);
  const snap = await getDoc(ref);
  if(!snap.exists()) throw new Error('Gönderi bulunamadı');
  const data: any = snap.data();
  if (data.authorId !== user.uid) {
    const profile = await getUserProfile(user.uid);
    if (profile?.role !== 'admin') throw new Error('Silme yetkiniz yok');
  }
  await deleteDoc(ref);
  // TODO: İlgili yorumları toplu silmek için (collection group query) eklenebilir.
};

// Post güncelle (sadece yazar veya admin)
export const updateCommunityPost = async (postId: string, updates: { content?: string; tags?: string[] }) => {
  const user = auth.currentUser; if(!user) throw new Error('Oturum gerekli');
  const ref = doc(db, POSTS_COL, postId);
  const snap = await getDoc(ref);
  if(!snap.exists()) throw new Error('Gönderi bulunamadı');
  const data: any = snap.data();
  if (data.authorId !== user.uid) {
    const profile = await getUserProfile(user.uid);
    if (profile?.role !== 'admin') throw new Error('Düzenleme yetkiniz yok');
  }
  const patch: any = { lastActivityAt: new Date().toISOString() };
  if (typeof updates.content === 'string') {
    const trimmed = updates.content.trim();
    if (!trimmed) throw new Error('İçerik boş olamaz');
    patch.content = trimmed;
  }
  if (updates.tags) {
    patch.tags = updates.tags.slice(0, 8);
  }
  await updateDoc(ref, patch);
};

// Belirli bir yazarın gönderilerini listele (profil entegrasyonu)
export const listCommunityPostsByAuthor = async (opts: { authorId: string; pageSize?: number; lastDoc?: DocumentSnapshot | null }) : Promise<PaginatedResult<CommunityPost>> => {
  const { authorId } = opts;
  const size = opts.pageSize || 6;
  if (!authorId) return { items: [], lastDoc: null, hasMore: false };
  let qBase: any = query(collection(db, POSTS_COL), where('authorId', '==', authorId), orderBy('lastActivityAt', 'desc'), limit(size));
  if (opts.lastDoc) qBase = query(qBase, startAfter(opts.lastDoc));
  try {
    const snap = await getDocs(qBase);
    const items: CommunityPost[] = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
    return { items, lastDoc: snap.docs[snap.docs.length-1] || null, hasMore: items.length === size };
  } catch (e: any) {
    if (e?.code === 'failed-precondition') {
      // Fallback: index yoksa tek seferlik küçük subset (ölçeklenmez) - kullanıcıya index oluşturma uyarısı
      try {
        const fbSnap = await getDocs(query(collection(db, POSTS_COL), orderBy('lastActivityAt','desc'), limit(size * 3)));
        const raw: CommunityPost[] = fbSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
        const filtered = raw.filter(p => (p as any).authorId === authorId).slice(0, size);
        return { items: filtered, lastDoc: null, hasMore: filtered.length === size };
      } catch {
        throw new Error('Yazar gönderilerini listelemek için (authorId + lastActivityAt) composite indeksi oluşturun.');
      }
    }
    throw e;
  }
};

// ================== Direkt Mesajlaşma (Basit) ==================

// Hash oluştur (iki user id'si sıralanıp birleştirilir)
const conversationHash = (a: string, b: string) => [a,b].sort().join('_');

// Konuşma başlat veya mevcut olanı döndür
export const getOrCreateConversation = async (otherUserId: string): Promise<string> => {
  const user = auth.currentUser; if(!user) throw new Error('Oturum gerekli');
  if (otherUserId === user.uid) throw new Error('Kendinle konuşma açılamaz');
  const hash = conversationHash(user.uid, otherUserId);
  // hash alanına göre arama (index gerekebilir)
  const convRef = collection(db, DM_CONV_COL);
  const qConv = query(convRef, where('participantsHash','==', hash), limit(1));
  const snap = await getDocs(qConv);
  const now = new Date().toISOString();
  if (!snap.empty) {
    return snap.docs[0].id;
  }
  // Participant profilleri minimal cache
  const selfProfile = await getUserProfile(user.uid);
  const otherProfile = await getUserProfile(otherUserId);
  const docRef = await addDoc(convRef, {
    participants: [user.uid, otherUserId],
    participantsHash: hash,
    createdAt: now,
    updatedAt: now,
    participantProfiles: {
      [user.uid]: { displayName: selfProfile?.displayName || 'Kullanıcı', username: (selfProfile as any)?.username || null, photoURL: selfProfile?.photoURL || null },
      [otherUserId]: { displayName: otherProfile?.displayName || 'Kullanıcı', username: (otherProfile as any)?.username || null, photoURL: otherProfile?.photoURL || null }
    }
  });
  return docRef.id;
};

// Mesaj gönder
export const sendDirectMessage = async (conversationId: string, content: string) => {
  const user = auth.currentUser; if(!user) throw new Error('Oturum gerekli');
  const trimmed = content.trim(); if(!trimmed) throw new Error('Mesaj boş');
  const now = new Date().toISOString();
  const msgRef = await addDoc(collection(db, DM_MSGS_COL), {
    conversationId,
    senderId: user.uid,
    content: trimmed,
    createdAt: now
  });
  await updateDoc(doc(db, DM_CONV_COL, conversationId), { lastMessage: trimmed, lastMessageAt: now, lastSenderId: user.uid, updatedAt: now });
  return msgRef.id;
};

// Konuşma listesi (kullanıcıya ait)
export const listUserConversations = async (limitSize = 20): Promise<DirectConversation[]> => {
  const user = auth.currentUser; if(!user) throw new Error('Oturum gerekli');
  const qConv = query(collection(db, DM_CONV_COL), where('participants','array-contains', user.uid), orderBy('updatedAt','desc'), limit(limitSize));
  const snap = await getDocs(qConv);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as any) } as DirectConversation));
};

// Mesajları listele (sayfalama basit)
export const listConversationMessages = async (conversationId: string, opts: { pageSize?: number; lastDoc?: DocumentSnapshot | null } = {}): Promise<PaginatedResult<DirectMessage>> => {
  const size = opts.pageSize || 40;
  let qBase: any = query(collection(db, DM_MSGS_COL), where('conversationId','==', conversationId), orderBy('createdAt','desc'), limit(size));
  if (opts.lastDoc) qBase = query(qBase, startAfter(opts.lastDoc));
  const snap = await getDocs(qBase);
  const items = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) } as DirectMessage));
  return { items: items.reverse(), lastDoc: snap.docs[snap.docs.length-1] || null, hasMore: items.length === size };
};

// Not: Gerçek zamanlı için bileşenlerde onSnapshot kullanılmalı.

// ================== Topluluk Kullanıcı Listesi ==================
export const listCommunityVisibleUsers = async (limitSize = 50): Promise<UserProfile[]> => {
  try {
    // privacy alanı public veya members olanlar
    let usersQ: any = query(collection(db, 'users'), where('privacy','in',['public','members']), limit(limitSize));
    const snap = await getDocs(usersQ);
    const list = snap.docs.map(d=> d.data() as UserProfile).filter(u=> (u as any).privacy === 'public' || (u as any).privacy === 'members');
    // İsim sıralaması (client side)
    return list.sort((a,b)=> (a.displayName||'').localeCompare(b.displayName||''));
  } catch(e:any) {
    // Index yoksa fallback: tüm kullanıcıları çekip filtrele (ölçeklenmez ancak geçici)
    if (e?.code === 'failed-precondition') {
      const all = await getAllUsers();
      return all.filter(u=> (u as any).privacy === 'public' || (u as any).privacy === 'members')
        .sort((a,b)=> (a.displayName||'').localeCompare(b.displayName||''))
        .slice(0, limitSize);
    }
    throw e;
  }
};
